// =========================================================================
// ErrorBoundary global — filet de rendu (incident n° 314)
// =========================================================================
//
// POURQUOI
//   Avant ce composant, `grep -rn "ErrorBoundary|componentDidCatch"` sur
//   frontend/src ne renvoyait RIEN : aucune erreur de rendu n'était confinée.
//   Une seule exception pendant le rendu — comme celle de l'incident n° 314 — démontait
//   l'arbre React entier et laissait un écran blanc, sans message, sans
//   moyen de repartir.
//
// CE QU'IL GARANTIT
//   Une erreur de rendu, quelle qu'en soit la cause, affiche désormais un
//   écran EXPLOITABLE : ce qui s'est passé, où, et deux issues (réessayer /
//   recharger). Il ne masque rien — l'erreur reste dans la console.
//
// CE QU'IL NE FAIT PAS
//   Il n'attrape ni les erreurs asynchrones (promesses rejetées), ni celles
//   des gestionnaires d'événements : React ne les fait pas remonter ici.
//   C'est pourquoi `safeToast` reste la première ligne de défense.

import React from "react";

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    this.setState({ info });
    // Trace conservée : le filet ne doit jamais rendre un bug invisible.
    console.error("[ErrorBoundary] erreur de rendu confinée :", error, info);
  }

  handleRetry = () => this.setState({ error: null, info: null });

  handleReload = () => window.location.reload();

  render() {
    const { error, info } = this.state;
    if (!error) return this.props.children;

    const composant = (info?.componentStack || "")
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean)[0] || null;

    return (
      <div
        role="alert"
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
          background: "var(--oh-bg)",
          color: "var(--oh-text)",
        }}
      >
        <div
          style={{
            maxWidth: 560,
            width: "100%",
            background: "var(--oh-bg-elevated)",
            border: "0.5px solid var(--oh-border)",
            borderRadius: 12,
            padding: 24,
          }}
        >
          <h1 style={{ fontSize: 18, fontWeight: 500, margin: 0, marginBottom: 8 }}>
            Cet écran n'a pas pu s'afficher
          </h1>
          <p style={{ color: "var(--oh-text-secondary)", lineHeight: 1.6, marginTop: 0, marginBottom: 16 }}>
            L'erreur a été contenue : le reste de l'application reste utilisable.
            Vous pouvez réessayer, ou recharger la page si le problème persiste.
          </p>

          <div
            style={{
              background: "var(--oh-stat-red-bg)",
              color: "var(--oh-stat-red-text)",
              borderRadius: 8,
              padding: 12,
              marginBottom: 16,
              fontSize: 13,
              lineHeight: 1.6,
              overflowX: "auto",
            }}
          >
            <div style={{ fontWeight: 500 }}>{String(error?.message || error)}</div>
            {composant && (
              <div style={{ marginTop: 4, opacity: 0.85 }}>Composant : {composant}</div>
            )}
          </div>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              onClick={this.handleRetry}
              data-testid="error-boundary-retry"
              style={{
                height: 36,
                padding: "0 16px",
                borderRadius: 8,
                border: "none",
                cursor: "pointer",
                fontSize: 14,
                background: "var(--oh-accent-solid)",
                color: "var(--oh-accent-contrast)", /* charte-ok: bouton primaire — accent-solid porteur */
              }}
            >
              Réessayer
            </button>
            <button
              onClick={this.handleReload}
              data-testid="error-boundary-reload"
              style={{
                height: 36,
                padding: "0 16px",
                borderRadius: 8,
                cursor: "pointer",
                fontSize: 14,
                background: "transparent",
                border: "1px solid var(--oh-border)",
                color: "var(--oh-text-secondary)",
              }}
            >
              Recharger la page
            </button>
          </div>
        </div>
      </div>
    );
  }
}
